/**
 * 運用詳細 — メンテタブ
 *
 * 4つの動作回数をStatisticカード2カラムGridで表示する。
 * APIから取得したデータをPropsで受け取り、ローディング/エラー/null値に対応する。
 * バッファ項目は表示しない。
 */

import React from 'react';
import { Card, Row, Col, Statistic, Spin, Alert, Empty } from 'antd';
import { GRID_CONFIG, RESPONSIVE_STYLES } from '@/shared/constants/breakpoints';
import { STATISTIC_COLORS } from '@/shared/constants/colors';
import type { MaintenanceDisplayItem } from '@/features/panels/constants/panelDetailTab.constants';

// ========== 定数 ==========

const MAINTENANCE_STATISTIC_PRECISION = 0;
const NULL_VALUE_PLACEHOLDER = '--';

// ========== Props ==========

interface MaintenanceTabProps {
  readonly isMobile: boolean;
  readonly maintenanceDisplayItems: readonly MaintenanceDisplayItem[];
  readonly isLoadingMaintenance: boolean;
  readonly maintenanceErrorMessage: string | null;
}

// ========== コンポーネント ==========

export const MaintenanceTab: React.FC<MaintenanceTabProps> = ({
  isMobile,
  maintenanceDisplayItems,
  isLoadingMaintenance,
  maintenanceErrorMessage,
}) => {
  const colProps = isMobile
    ? GRID_CONFIG.detailView.mobile
    : GRID_CONFIG.detailView.desktop;

  const responsiveGutter = isMobile
    ? RESPONSIVE_STYLES.gutter.mobile
    : RESPONSIVE_STYLES.gutter.desktop;

  if (isLoadingMaintenance) {
    return (
      <div className="flex justify-center py-8">
        <Spin tip="メンテナンスデータを読み込み中..." />
      </div>
    );
  }

  if (maintenanceErrorMessage !== null) {
    return (
      <Alert
        message="データ取得エラー"
        description={maintenanceErrorMessage}
        type="warning"
        showIcon
      />
    );
  }

  if (maintenanceDisplayItems.length === 0) {
    return <Empty description="送信データがありません" />;
  }

  return (
    <Row gutter={responsiveGutter}>
      {maintenanceDisplayItems.map((item) => (
        <Col key={item.name} {...colProps} className="mb-2">
          <Card>
            <Statistic
              title={item.name}
              value={item.value !== null ? item.value : NULL_VALUE_PLACEHOLDER}
              precision={item.value !== null ? MAINTENANCE_STATISTIC_PRECISION : undefined}
              suffix={item.value !== null ? item.unit : ''}
              valueStyle={{ color: STATISTIC_COLORS.TOTAL }}
            />
          </Card>
        </Col>
      ))}
    </Row>
  );
};
